/**
 * Current-user (`/api/admin/me/*`) request + response schemas (issue #274 —
 * multi-tenancy Phase 3).
 *
 * The endpoints shipped in Phase 3 are:
 *
 *   GET  /api/admin/me/orgs          — list the user's org memberships
 *   POST /api/admin/me/current-org   — switch the user's current org
 *
 * Phase 4 (#275) will build the SPA org-switcher dropdown that consumes the
 * GET endpoint and POSTs to the switch endpoint. The shapes here are the
 * stable contract between the two phases — do not edit the response shape
 * without coordinating with the Phase 4 UI work.
 */

import { z } from "zod";

/**
 * Body schema for `POST /api/admin/me/current-org`.
 *
 * `.strict()` rejects unknown fields so a typo in the SPA (e.g.
 * `orgId` instead of `organizationId`) surfaces as a 422 instead of being
 * silently dropped and falling through to the resolver's "self-heal to
 * oldest" path.
 */
export const setCurrentOrgSchema = z
  .object({
    organizationId: z.string().trim().min(1),
  })
  .strict();
export type SetCurrentOrgInput = z.infer<typeof setCurrentOrgSchema>;

/**
 * Single org as it appears on `GET /api/admin/me/orgs`. `role` mirrors the
 * `OrganizationMembership.role` column (OWNER | MEMBER as of Phase 1 — see
 * `api/src/orgs/roles.ts`). Kept as a string so future role literals
 * introduced by Phase 4/5 (e.g. VIEWER) don't require an API contract
 * change.
 */
export type MeOrgMembership = {
  id: string;
  name: string;
  slug: string;
  role: string;
  joinedAt: string;
};

/**
 * Response shape for `GET /api/admin/me/orgs`.
 *
 *   `orgs` — every org the calling user is a member of, ordered oldest
 *            membership first so the user's "first" org sits at the top of
 *            the switcher dropdown (matches `resolveCurrentOrg`'s self-heal
 *            preference).
 *   `currentOrganizationId` — the user's currently-active org pointer.
 *                              Always non-null after Phase 1 backfill +
 *                              signup auto-provision, but `null` is allowed
 *                              for forward compatibility (a deletion flow
 *                              that orphans the user could leave it unset).
 */
export type MeOrgsResponse = {
  orgs: MeOrgMembership[];
  currentOrganizationId: string | null;
};

/**
 * Response shape for `POST /api/admin/me/current-org`. Mirror of the
 * pointer that just got written — gives the SPA the value to update its
 * in-memory state without a round-trip back to `GET /me/orgs`.
 */
export type SetCurrentOrgResponse = {
  currentOrganizationId: string;
};
